import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTitle } from "../../../Shared/useTitle";

import ManageAppsStyles from "./manageApps.module.scss";
import ManageAppCardGridStyles from "./manage-app-card/manageAppCard.module.scss";

import ManageAppCard from "./manage-app-card/manageAppCard";
import CreateAppPopUp from "./create-app-pop-up/createAppPopUp";
import Loading from "../../../Shared/loading/loading";
import Toast from "../../../Shared/toast/toast";
import EmptyState from "../../../Shared/empty-state/emptyState";
import RenderTooltip from "../../../Shared/tooltip/tooltip";

import { ACCENTCOLOR } from "../../../Shared/colors";
import emptyStateStyle from "../../../Shared/empty-state/emptyState.module.scss";

import { callApi } from "../../../api/fetch";
import {
  APPS_PAGE_LIMIT,
  mapAppsWithKeyCounts,
  unwrapListData,
  unwrapListMetadata,
} from "../../../utils/dashboardMappers";

import { App } from "../../../constants/app";
import { noContent } from "../../../constants/emptyStatesVariables";
import {
  ONBOARDING_STATUS,
  normalizeOnboardingStatus,
} from "../../../constants/onboardingStatuses";
import { buttonStyles, StyledButton } from "../../../Shared/button/button";

function ManageApps() {
  const history = useNavigate();
  const [showCreateApp, setShowCreateApp] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [orgInfoLoading, setOrgInfoLoading] = useState(false);
  const [apps, setApps] = useState<App[]>([]);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);

  const org_id = localStorage.getItem("organisation_id");
  const [onboardingStatus, setOnboardingStatus] = useState("");

  const fetchAppsPage = useCallback(async (offset: number, append: boolean) => {
    const data = await callApi(
      `/apps?limit=${APPS_PAGE_LIMIT}&offset=${offset}`,
      { method: "GET" }
    );
    const pageApps = unwrapListData<App>(data, "apps");
    const metadata = unwrapListMetadata(data);
    const total = metadata?.total ?? pageApps.length;

    const withKeyCounts = mapAppsWithKeyCounts(pageApps);
    setApps((prev) => (append ? [...prev, ...withKeyCounts] : withKeyCounts));
    setHasMore(offset + pageApps.length < total);
  }, []);

  const loadInitialApps = useCallback(async () => {
    setLoading(true);
    try {
      await fetchAppsPage(0, false);
    } catch (err) {
      toastTimeout(true, "error", (err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [fetchAppsPage]);

  const loadMoreApps = useCallback(async () => {
    if (loading || loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      await fetchAppsPage(apps.length, true);
    } catch (err) {
      toastTimeout(true, "error", (err as Error).message);
    } finally {
      setLoadingMore(false);
    }
  }, [apps.length, fetchAppsPage, hasMore, loading, loadingMore]);

  useEffect(() => {
    loadInitialApps();
  }, [loadInitialApps]);

  useEffect(() => {
    const target = loadMoreRef.current;
    if (!target || !hasMore || loading || loadingMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          loadMoreApps();
        }
      },
      { rootMargin: "240px" }
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, [hasMore, loadMoreApps, loading, loadingMore]);

  // getting org info api
  useEffect(() => {
    setOrgInfoLoading(true);
    callApi(`/organisation`, { method: "GET" })
      .then((response) => {
        localStorage.setItem("country_code", response.country_code);
        setOnboardingStatus(
          normalizeOnboardingStatus(response.onboarding_status)
        );
        setOrgInfoLoading(false);
      })
      .catch((error) => {
        setOrgInfoLoading(false);
        toastTimeout(true, "error", error.message);
        // TODO: add sentry error
      });
  }, [org_id]);

  function toastTimeout(
    showToastValue: boolean,
    toastType: "success" | "error",
    toastMessage: string | string[],
    heading?: string
  ) {
    if (!showToastValue) return;

    // Show toast via react-toastify
    Toast(
      toastType,
      toastMessage,
      heading || (toastType === "success" ? "Success" : "Error")
    );
  }

  function handleCreateAppCancel() {
    setShowCreateApp(false);
  }
  function handleMoreInfo(value: App) {
    if (!value?.app_id) {
      toastTimeout(true, "error", "Unable to open this app. App ID is missing.");
      return;
    }
    history(`/application/manage-apps/${value.app_id}`);
  }
  useTitle("Zoop Customer Platform | Apps Listings");
  return (
    <div>
      {loading || orgInfoLoading ? (
        <div
          style={{ height: "100vh" }}
          className="d-flex align-items-center justify-content-center"
        >
          <Loading loadingColor={ACCENTCOLOR} />
        </div>
      ) : (
        <div
          className="container-fluid px-4"
          style={{ background: "#F3F4F6", height: "100%", minHeight: "100vh" }}
        >
          <div className="d-flex align-items-center align-content-end justify-content-end pt-4">
            <div className="mb-4">
              {normalizeOnboardingStatus(onboardingStatus) !==
              ONBOARDING_STATUS.PLAN_SUBSCRIBED ? (
                <RenderTooltip
                  placement="bottom"
                  title="Subscribe a plan to create an App"
                >
                  <StyledButton
                    id="manage-apps-create-disabled"
                    title=" Create App"
                    type="submit"
                    disabled={
                      normalizeOnboardingStatus(onboardingStatus) !==
                      ONBOARDING_STATUS.PLAN_SUBSCRIBED
                    }
                    style={buttonStyles.primary.hover}
                    hoverStyle={buttonStyles.primary.hover}
                  >
                    Create App
                  </StyledButton>
                </RenderTooltip>
              ) : (
                <StyledButton
                  id="manage-apps-create"
                  title=" Create App"
                  type="submit"
                  onClick={() => setShowCreateApp(true)}
                  disabled={
                    normalizeOnboardingStatus(onboardingStatus) !==
                    ONBOARDING_STATUS.PLAN_SUBSCRIBED
                  }
                  style={buttonStyles.primary.hover}
                  hoverStyle={buttonStyles.primary.hover}
                >
                  Create App
                </StyledButton>
              )}
            </div>
          </div>
          <div className={ManageAppCardGridStyles.cardGrid}>
              {apps.length !== 0 ? (
                <>
                  {apps.map((app: App) => (
                    <ManageAppCard
                      app={app}
                      key={app.app_id as string}
                      click={() => handleMoreInfo(app)}
                    />
                  ))}
                  {hasMore ? (
                    <div
                      ref={loadMoreRef}
                      className="d-flex w-100 justify-content-center py-4"
                      style={{ gridColumn: "1 / -1" }}
                    >
                      {loadingMore ? (
                        <Loading loadingColor={ACCENTCOLOR} />
                      ) : null}
                    </div>
                  ) : null}
                </>
              ) : (
                <div
                  // Corrected: Using ManageAppsStyles for emptyState
                  className={`${ManageAppsStyles.emptyState} d-flex align-items-center justify-content-center`}
                >
                  <EmptyState type={noContent}>
                    <div className={"py-3"}>
                      <p
                        className={`${emptyStateStyle.emptyStateHeadingText} mb-0`}
                      >
                        No Apps found
                      </p>
                    </div>
                  </EmptyState>
                </div>
              )}
          </div>
        </div>
      )}

      {showCreateApp ? (
        <CreateAppPopUp onCancel={handleCreateAppCancel} />
      ) : null}
    </div>
  );
}

export default ManageApps;
